import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { LinkSimple, Copy } from "@phosphor-icons/react";
import { formatET } from "../lib/dateUtils";
import { DashboardLayout } from "../components/layout/DashboardLayout";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Textarea } from "../components/ui/textarea";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { useAuth } from "../context/AuthContext";
import { getClientNavItems } from "../modules/clientNav";
import { api, formatApiErrorDetail } from "../lib/api";
import { SUPPORT_EMAIL } from "../constants/brand";
import { WhatDoesThisMean } from "../components/shared/WhatDoesThisMean";

const SOURCE_LABEL = { trust: "Trust & Asset Protection", funding: "Business Funding" };

const COMMISSION_EXPLAINERS = {
  pending: "Your referral is confirmed and your commission is queued for payment once the deal closes.",
  approved: "IGC has approved your commission — it's scheduled for payout.",
  paid: "Your commission has been paid out. Thank you for the referral!",
  rejected: "This referral didn't qualify for a commission payout this time.",
};

const EMPTY_FUNDING_FORM = { business_name: "", contact_name: "", phone: "", email: "", amount_requested: "", notes: "" };
const EMPTY_TRUST_FORM = { client_name: "", contact_name: "", phone: "", email: "", trust_type: "", estimated_trust_value: "", notes: "" };

export default function ClientAffiliatePage() {
  const { user } = useAuth();
  const [referrals, setReferrals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fundingOpen, setFundingOpen] = useState(false);
  const [trustOpen, setTrustOpen] = useState(false);
  const [fundingForm, setFundingForm] = useState(EMPTY_FUNDING_FORM);
  const [trustForm, setTrustForm] = useState(EMPTY_TRUST_FORM);
  const [links, setLinks] = useState(null);

  const fetchLinks = async () => {
    try {
      const { data } = await api.get("/affiliate/referral-links");
      setLinks(data);
    } catch {
      // referral links are a nice-to-have — fail silently
    }
  };

  const fetchReferrals = async () => {
    try {
      const { data } = await api.get("/affiliate/referrals");
      setReferrals(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReferrals();
    fetchLinks();
    const interval = setInterval(fetchReferrals, 20000);
    return () => clearInterval(interval);
  }, []);

  const copyLink = (url) => {
    navigator.clipboard.writeText(url).then(() => toast.success("Link copied")).catch(() => toast.error("Couldn't copy"));
  };

  const submitFunding = async (e) => {
    e.preventDefault();
    try {
      await api.post("/affiliate/funding-lead", { ...fundingForm, amount_requested: parseFloat(fundingForm.amount_requested) || 0 });
      toast.success("Funding lead submitted to IGC");
      setFundingOpen(false);
      setFundingForm(EMPTY_FUNDING_FORM);
      fetchReferrals();
    } catch (e2) {
      toast.error(formatApiErrorDetail(e2.response?.data?.detail));
    }
  };

  const submitTrust = async (e) => {
    e.preventDefault();
    try {
      await api.post("/affiliate/trust-lead", { ...trustForm, estimated_trust_value: parseFloat(trustForm.estimated_trust_value) || 0 });
      toast.success("Trust lead submitted to IGC");
      setTrustOpen(false);
      setTrustForm(EMPTY_TRUST_FORM);
      fetchReferrals();
    } catch (e2) {
      toast.error(formatApiErrorDetail(e2.response?.data?.detail));
    }
  };

  const pendingCount = referrals.filter((r) => r.commission_status === "pending").length;
  const totalEarned = referrals.reduce((sum, r) => sum + (r.commission_status === "paid" || r.commission_status === "approved" ? r.commission_amount || 0 : 0), 0);

  const navItems = getClientNavItems(user?.unlocked_modules, user?.role);

  return (
    <DashboardLayout navItems={navItems} moduleName="Sales Reps" moduleBackPath="/client">
      <div data-testid="client-affiliate-page" className="max-w-3xl">
        <h1 className="font-serif text-3xl text-igc-cream tracking-tight mb-2" title="Refer new Trust & Asset Protection or Business Funding clients to IGC and earn 20% commission on closed deals.">Sales Reps</h1>
        <p className="text-igc-muted text-sm mb-8">Earn 20% commission referring Trust & Asset Protection or Business Funding deals to IGC.</p>

        <div className="grid grid-cols-3 gap-4 mb-8">
          <div className="bg-igc-surface border border-igc-gold/15 p-5" data-testid="affiliate-total-referrals">
            <p className="text-igc-muted text-xs uppercase tracking-widest mb-2" title="Total leads you've referred to IGC.">Referrals</p>
            <p className="font-serif text-2xl text-igc-cream">{referrals.length}</p>
          </div>
          <div className="bg-igc-surface border border-igc-gold/15 p-5" data-testid="affiliate-pending-count">
            <p className="text-igc-muted text-xs uppercase tracking-widest mb-2" title="Referrals still awaiting a commission decision.">Pending</p>
            <p className="font-serif text-2xl text-igc-goldText">{pendingCount}</p>
          </div>
          <div className="bg-igc-surface border border-igc-gold/15 p-5" data-testid="affiliate-earned-commission">
            <p className="text-igc-muted text-xs uppercase tracking-widest mb-2" title="Total commission you've earned or been approved for so far.">Earned</p>
            <p className="font-serif text-2xl text-igc-success">${totalEarned.toLocaleString()}</p>
          </div>
        </div>

        {links && (
          <div className="bg-igc-surface border border-igc-gold/15 p-5 mb-8" data-testid="affiliate-referral-links-card">
            <p className="text-igc-cream font-serif text-lg mb-1 flex items-center gap-2"><LinkSimple size={18} weight="duotone" className="text-igc-goldText" /> Your Referral Links</p>
            <p className="text-igc-muted text-sm mb-3">Share these — every signup and deal through them is automatically credited to you.</p>
            <div className="space-y-2">
              <div className="flex items-center gap-2 bg-igc-surfaceLight px-3 py-2 text-xs" data-testid="affiliate-referral-link-funding">
                <span className="text-igc-muted w-16 flex-shrink-0">Funding</span>
                <span className="text-igc-cream truncate flex-1">{links.funding}</span>
                <button data-testid="affiliate-referral-link-funding-copy" onClick={() => copyLink(links.funding)} title="Copies your Business Funding referral link to your clipboard." className="text-igc-goldText hover:text-igc-gold flex-shrink-0"><Copy size={14} /></button>
              </div>
              <div className="flex items-center gap-2 bg-igc-surfaceLight px-3 py-2 text-xs" data-testid="affiliate-referral-link-trust">
                <span className="text-igc-muted w-16 flex-shrink-0">Trust</span>
                <span className="text-igc-cream truncate flex-1">{links.trust}</span>
                <button data-testid="affiliate-referral-link-trust-copy" onClick={() => copyLink(links.trust)} title="Copies your Trust & Asset Protection referral link to your clipboard." className="text-igc-goldText hover:text-igc-gold flex-shrink-0"><Copy size={14} /></button>
              </div>
            </div>
          </div>
        )}

        <div className="flex items-center gap-3 mb-8 flex-wrap">
          <Button data-testid="open-funding-lead-button" onClick={() => setFundingOpen(true)} title="Opens a form to submit a new Business Funding referral." className="bg-igc-gold hover:bg-igc-goldHover text-black">
            Submit Funding Lead
          </Button>
          <Button data-testid="open-trust-lead-button" onClick={() => setTrustOpen(true)} variant="outline" title="Opens a form to submit a new Trust & Asset Protection referral." className="border-igc-gold text-igc-goldText hover:bg-igc-gold hover:text-black">
            Submit Trust Lead
          </Button>
        </div>

        <div className="space-y-3">
          {!loading && referrals.length === 0 && <p className="text-igc-muted text-sm">You haven't submitted any leads yet.</p>}
          {referrals.map((r) => (
            <motion.div key={r.id} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="bg-igc-surface border border-igc-gold/15 p-5 flex items-center justify-between gap-4 flex-wrap" data-testid={`affiliate-referral-${r.id}`}>
              <div>
                <p className="text-igc-cream text-sm">{r.name} <span className="text-igc-muted text-xs">({SOURCE_LABEL[r.source] || r.source})</span></p>
                <p className="text-igc-muted text-xs">{r.contact} — {formatET(r.created_at, "MMM d, yyyy")}</p>
              </div>
              <div className="text-right">
                <span className="text-[10px] px-2 py-1 tracking-wide bg-igc-gold/10 text-igc-goldText" data-testid={`affiliate-referral-stage-${r.id}`}>{r.stage}</span>
                <p
                  className={`text-xs mt-1 ${r.commission_status === "paid" ? "text-igc-success" : r.commission_status === "rejected" ? "text-igc-danger" : "text-igc-muted"}`}
                  data-testid={`affiliate-referral-commission-${r.id}`}
                >
                  {r.commission_status === "paid" && `Paid $${(r.commission_amount || 0).toLocaleString()}`}
                  {r.commission_status === "approved" && `Approved $${(r.commission_amount || 0).toLocaleString()}`}
                  {r.commission_status === "pending" && "Awaiting review"}
                  {r.commission_status === "rejected" && "Not approved"}
                  <WhatDoesThisMean testId={`affiliate-commission-explainer-${r.id}`} text={COMMISSION_EXPLAINERS[r.commission_status] || "Your referral is being reviewed by our team."} />
                </p>
              </div>
            </motion.div>
          ))}
        </div>

        <Dialog open={fundingOpen} onOpenChange={setFundingOpen}>
          <DialogContent className="bg-igc-surface border-igc-gold/20 text-igc-cream" data-testid="funding-lead-modal">
            <DialogHeader><DialogTitle className="font-serif">Submit Funding Lead</DialogTitle></DialogHeader>
            <form onSubmit={submitFunding} className="space-y-3">
              <Input data-testid="funding-lead-business-name-input" required placeholder="Business name" title="The legal name of the business seeking funding." value={fundingForm.business_name} onChange={(e) => setFundingForm({ ...fundingForm, business_name: e.target.value })} className="bg-igc-surfaceLight border-igc-gold/20" />
              <Input data-testid="funding-lead-contact-name-input" required placeholder="Contact name" title="The name of the person we should contact about this funding request." value={fundingForm.contact_name} onChange={(e) => setFundingForm({ ...fundingForm, contact_name: e.target.value })} className="bg-igc-surfaceLight border-igc-gold/20" />
              <Input data-testid="funding-lead-phone-input" required placeholder="Phone number" title="A phone number to reach this contact." value={fundingForm.phone} onChange={(e) => setFundingForm({ ...fundingForm, phone: e.target.value })} className="bg-igc-surfaceLight border-igc-gold/20" />
              <Input data-testid="funding-lead-email-input" required type="email" placeholder="Email" title="An email address to reach this contact." value={fundingForm.email} onChange={(e) => setFundingForm({ ...fundingForm, email: e.target.value })} className="bg-igc-surfaceLight border-igc-gold/20" />
              <Input data-testid="funding-lead-amount-input" required type="number" min="0" placeholder="Amount requested ($)" title="How much funding this business is looking to raise." value={fundingForm.amount_requested} onChange={(e) => setFundingForm({ ...fundingForm, amount_requested: e.target.value })} className="bg-igc-surfaceLight border-igc-gold/20" />
              <Textarea data-testid="funding-lead-notes-input" placeholder="Notes (optional)" title="Any extra context that will help IGC handle this referral." value={fundingForm.notes} onChange={(e) => setFundingForm({ ...fundingForm, notes: e.target.value })} className="bg-igc-surfaceLight border-igc-gold/20" />
              <Button data-testid="funding-lead-submit-button" type="submit" title="Submits this referral to IGC." className="w-full bg-igc-gold hover:bg-igc-goldHover text-black">Submit Funding Lead</Button>
            </form>
          </DialogContent>
        </Dialog>

        <Dialog open={trustOpen} onOpenChange={setTrustOpen}>
          <DialogContent className="bg-igc-surface border-igc-gold/20 text-igc-cream" data-testid="trust-lead-modal">
            <DialogHeader><DialogTitle className="font-serif">Submit Trust Lead</DialogTitle></DialogHeader>
            <form onSubmit={submitTrust} className="space-y-3">
              <Input data-testid="trust-lead-client-name-input" required placeholder="Client name" title="The name of the client who needs trust or asset protection." value={trustForm.client_name} onChange={(e) => setTrustForm({ ...trustForm, client_name: e.target.value })} className="bg-igc-surfaceLight border-igc-gold/20" />
              <Input data-testid="trust-lead-contact-name-input" required placeholder="Contact name" title="The name of the person we should contact." value={trustForm.contact_name} onChange={(e) => setTrustForm({ ...trustForm, contact_name: e.target.value })} className="bg-igc-surfaceLight border-igc-gold/20" />
              <Input data-testid="trust-lead-phone-input" required placeholder="Phone number" title="A phone number to reach this contact." value={trustForm.phone} onChange={(e) => setTrustForm({ ...trustForm, phone: e.target.value })} className="bg-igc-surfaceLight border-igc-gold/20" />
              <Input data-testid="trust-lead-email-input" required type="email" placeholder="Email" title="An email address to reach this contact." value={trustForm.email} onChange={(e) => setTrustForm({ ...trustForm, email: e.target.value })} className="bg-igc-surfaceLight border-igc-gold/20" />
              <Input data-testid="trust-lead-trust-type-input" required placeholder="Trust type" title="The kind of trust or asset protection structure being considered." value={trustForm.trust_type} onChange={(e) => setTrustForm({ ...trustForm, trust_type: e.target.value })} className="bg-igc-surfaceLight border-igc-gold/20" />
              <Input data-testid="trust-lead-value-input" required type="number" min="0" placeholder="Estimated trust value ($)" title="An estimate of the total assets to be protected under this trust." value={trustForm.estimated_trust_value} onChange={(e) => setTrustForm({ ...trustForm, estimated_trust_value: e.target.value })} className="bg-igc-surfaceLight border-igc-gold/20" />
              <Textarea data-testid="trust-lead-notes-input" placeholder="Notes (optional)" title="Any extra context that will help IGC handle this referral." value={trustForm.notes} onChange={(e) => setTrustForm({ ...trustForm, notes: e.target.value })} className="bg-igc-surfaceLight border-igc-gold/20" />
              <Button data-testid="trust-lead-submit-button" type="submit" title="Submits this referral to IGC." className="w-full bg-igc-gold hover:bg-igc-goldHover text-black">Submit Trust Lead</Button>
            </form>
          </DialogContent>
        </Dialog>

        <p className="text-igc-muted text-xs text-center mt-10">Questions? Contact us at {SUPPORT_EMAIL}</p>
      </div>
    </DashboardLayout>
  );
}
